import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { hasCompletedSetup } from '../utils/authHelpers';
import { supabase } from '../services/supabase';
import Spinner from '../../shared/components/ui/Spinner';

/**
 * ProtectedRoute - Wrapper for routes that require authentication
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to render if authorized
 * @param {string|string[]} props.requireRole - Required role(s) to access this route
 * @param {string} props.requireClubId - Required club ID (for club-scoped routes)
 * @param {string} props.requireTeamId - Required team ID (for team-scoped routes)
 * @param {boolean} props.requireSetup - Whether user must have completed setup
 * @param {string} props.redirectTo - Where to redirect if not authorized
 */
export const ProtectedRoute = ({
  children,
  requireRole = null,
  requireClubId = null,
  requireTeamId = null,
  requireSetup = true,
  redirectTo = '/login',
}) => {
  const { user, loading, userRoles } = useAuth();
  const location = useLocation();
  const params = useParams();
  const permissions = usePermissions();
  const [teamData, setTeamData] = useState(null);
  const [teamLoading, setTeamLoading] = useState(false);

  // Extract clubId and teamId from URL params if not explicitly provided
  const clubId = requireClubId || params.clubId;
  const teamId = requireTeamId || params.teamId;

  // Fetch team data if we need to check team access for a club admin
  useEffect(() => {
    if (teamId && !loading && userRoles.length > 0) {
      const hasClubAdminRole = userRoles.some(r => r.role === 'club_admin' || r.role === 'system_admin');

      // Fetch team data if user is club/system admin (they might need to access via club)
      if (hasClubAdminRole) {
        setTeamLoading(true);
        supabase
          .from('teams')
          .select('id, club_id')
          .eq('id', teamId)
          .single()
          .then(({ data, error }) => {
            if (!error && data) {
              setTeamData(data);
            } else {
              setTeamData(null);
              if (error) {
                console.error('Error fetching team data:', error);
              }
            }
            setTeamLoading(false);
          })
          .catch(err => {
            console.error('Error fetching team data:', err);
            setTeamData(null);
            setTeamLoading(false);
          });
      } else {
        setTeamData(null);
        setTeamLoading(false);
      }
    } else {
      // Only set loading to false if we're actually done loading auth
      if (!loading) {
        setTeamLoading(false);
      }
    }
  }, [teamId, loading, userRoles]);

  // Show loading state
  if (loading || teamLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!user) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // User hasn't completed setup
  if (requireSetup && !hasCompletedSetup(userRoles)) {
    // Don't redirect if already on setup page
    if (location.pathname !== '/setup') {
      return <Navigate to="/setup" state={{ from: location }} replace />;
    }
  }

  // Check role requirements
  if (requireRole) {
    const roles = Array.isArray(requireRole) ? requireRole : [requireRole];
    const hasRequiredRole = roles.some((role) => {
      if (role === 'system_admin') {
        return permissions.isSystemAdmin();
      }

      if (role === 'club_admin') {
        // If checking club_admin for a team route, verify the club owns the team
        if (teamId && teamData) {
          return permissions.isClubAdmin(teamData.club_id);
        }
        return permissions.isClubAdmin(clubId);
      }

      if (role === 'team_captain') {
        return permissions.isTeamCaptain(teamId);
      }

      if (role === 'player') {
        return permissions.isPlayer(teamId);
      }

      return false;
    });

    if (!hasRequiredRole) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // Check club access
  if (clubId && !permissions.canAccessClub(clubId)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Check team access
  if (teamId) {
    // First check direct team access
    const hasDirectTeamAccess = permissions.canAccessTeam(teamId);
    const isSystemAdmin = permissions.isSystemAdmin();

    // If user has direct access or is system admin, grant immediately
    if (!hasDirectTeamAccess && !isSystemAdmin) {
      // User might be club admin - need to check if team belongs to their club
      const hasClubAdminRole = userRoles.some(r => r.role === 'club_admin');

      if (hasClubAdminRole) {
        // Club admin needs team data to verify access
        if (!teamData) {
          // Wait for team data to load
          return (
            <div className="min-h-screen flex items-center justify-center">
              <Spinner />
            </div>
          );
        }

        const hasClubAdminAccess = userRoles.some(
          r => r.role === 'club_admin' && r.club_id === teamData.club_id
        );

        if (!hasClubAdminAccess) {
          return <Navigate to="/unauthorized" replace />;
        }
      } else {
        // No direct access, not club admin, not system admin
        return <Navigate to="/unauthorized" replace />;
      }
    }
  }

  // All checks passed - render children
  return children;
};

/**
 * RequireAuth - Simple wrapper that just requires authentication (no role checks)
 */
export const RequireAuth = ({ children, redirectTo = '/login' }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!user) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  return children;
};

/**
 * RequireRole - Wrapper that requires specific role(s)
 */
export const RequireRole = ({ children, role, clubId, teamId }) => {
  return (
    <ProtectedRoute
      requireRole={role}
      requireClubId={clubId}
      requireTeamId={teamId}
    >
      {children}
    </ProtectedRoute>
  );
};

export default ProtectedRoute;
